import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { DealerIntakeForm, serializeIntake } from "@/components/DealerIntakeForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, permanent self-signup page for new dealers. Collects the full
 * dealer intake (dealer info + N locations + N ACH groups), creates the
 * dealer record with the admin-configured default packet, then hands the
 * applicant into the existing dealer e-sign flow.
 */
export default function DealerApply() {
  const [form, setForm] = useState({
    dealer_name: "", dba: "", physical_address: "", phone: "", ein: "", dealer_principal: "",
    contact_name: "", contact_email: "", contact_phone: "", notes: "",
    locations: [], ach_groups: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submit = async () => {
    for (const k of ["dealer_name", "contact_name", "contact_email"]) {
      if (!form[k] || !form[k].trim()) { toast.error("Dealer name, your name, and email are required"); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/dealers/self-apply`, serializeIntake(form));
      setResult(data);
      if (data.has_forms && data.sign_token) {
        setTimeout(() => navigate(`/sign-dealer/${data.sign_token}`), 1600);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (result && result.has_forms) {
    return (
      <FullScreen>
        <Card className="max-w-md w-full p-10 bg-white border border-[#E9ECEF] rounded-sm text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <h1 className="mt-3 text-2xl font-black text-[#0B132B]">Redirecting you to the packet…</h1>
          <p className="mt-3 text-sm text-[#6C757D]">If nothing happens, <a href={`/sign-dealer/${result.sign_token}`} className="text-[#0B132B] underline">click here to continue</a>.</p>
        </Card>
      </FullScreen>
    );
  }
  if (result && !result.has_forms) {
    return (
      <FullScreen>
        <Card className="max-w-lg w-full p-10 bg-white border border-[#E9ECEF] rounded-sm text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Received</div>
          <h1 className="mt-2 text-2xl font-black text-[#0B132B]">Thanks — we're on it</h1>
          <p className="mt-3 text-sm text-[#6C757D] leading-relaxed">
            An Advantage Warranty Group representative will prepare your dealer packet and email it to <strong className="text-[#0B132B]">{form.contact_email}</strong> within one business day.
          </p>
        </Card>
      </FullScreen>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            <ShieldCheck size={13} /> Dealer Sign-Up
          </div>
          <h1 className="mt-2 text-4xl font-black text-[#0B132B] tracking-tight">Get started with Advantage</h1>
          <p className="mt-3 text-sm text-[#6C757D] max-w-xl mx-auto leading-relaxed">
            Tell us about your dealership, list every rooftop, and add ACH details. As soon as you submit we'll load your agreement packet so you can review + e-sign it right away.
          </p>
        </div>

        <DealerIntakeForm value={form} onChange={setForm} />

        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6 mt-6">
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Anything we should know? (optional)</Label>
          <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. programs you're interested in, current provider, timelines" className="mt-1" data-testid="dealer-apply-notes" />
        </Card>

        <div className="mt-6">
          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-12 text-sm uppercase tracking-[0.16em] font-bold"
            data-testid="dealer-apply-submit"
          >
            {submitting ? "Submitting…" : "Continue to packet"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      {children}
    </div>
  );
}
